<template>
  <div class="page">
    <!-- HEADER -->
    <div class="page-head">
      <div>
        <h1>KYC Requests</h1>
      </div>

      <div class="actions">
        <div class="search-mini">
          <i class="bx bx-search"></i>
          <input v-model="query" type="text" placeholder="Search user name, phone, id..." />
        </div>

        <select v-model="statusFilter" class="select">
          <option value="">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>

        <select v-model="docFilter" class="select">
          <option value="">All Documents</option>
          <option value="AADHAR">Aadhar</option>
          <option value="PAN">PAN</option>
        </select>

        <select v-model="sortBy" class="select">
          <option value="NEWEST">Newest First</option>
          <option value="OLDEST">Oldest First</option>
        </select>

        <button class="btn ghost" @click="exportExcel">
          <i class="bx bx-export"></i>
          Export
        </button>
      </div>
    </div>

    <!-- STATS -->
    <div class="grid cols-4">
      <div class="card stat">
        <div class="card-title">
          <strong>Total Requests</strong>
          <span class="badge primary">LIVE</span>
        </div>
        <div class="metric">{{ requests.length }}</div>
        <div class="mini">All KYC submissions</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Pending</strong>
          <span class="badge warning">ACTION</span>
        </div>
        <div class="metric">{{ pendingCount }}</div>
        <div class="mini">Need review</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Approved</strong>
          <span class="badge success">OK</span>
        </div>
        <div class="metric">{{ approvedCount }}</div>
        <div class="mini">Verified accounts</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Rejected</strong>
          <span class="badge warning">CHECK</span>
        </div>
        <div class="metric">{{ rejectedCount }}</div>
        <div class="mini">Need resubmission</div>
      </div>
    </div>

    <!-- TABLE -->
    <div class="card" style="margin-top: 14px;">
      <div class="table-head">
        <div class="table-title">
          <strong>Requests</strong>
          <span class="muted">Showing {{ filtered.length }} records</span>
        </div>

        <button class="btn primary" @click="toast.info('Auto verification integration later')">
          <i class="bx bx-shield-quarter"></i>
          Auto Verify
        </button>
      </div>

      <!-- Desktop Table -->
      <div class="table-wrap desktop-only">
        <table class="table">
          <thead>
            <tr>
              <th>USER</th>
              <th>DOCUMENT</th>
              <th>STATUS</th>
              <th>SUBMITTED</th>
              <th>REVIEWED BY</th>
              <th style="text-align:right;">ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="r in filtered" :key="r.user_id">
              <td>
                <div class="usr">
                  <div class="avatar" v-if="r.user_fname!=null">{{ r.user_fname.charAt(0).toUpperCase() }}</div>
                  <div class="usr-meta">
                    <div class="usr-name">{{ r.user_fname }} {{ r.user_lname }}</div>
                    <div class="row-muted">{{ r.user_email }} • {{ r.user_mobile }}</div>
                  </div>
                </div>
              </td>

              <td>
                <div class="doc">
                  <span class="doc-pill">
                    <i class="bx bx-id-card"></i>
                    {{ r.doc_type === "AADHAR" ? "Aadhar Card" : "PAN Card" }}
                  </span>
                  <div class="row-muted">{{ r.doc_no }}</div>
                </div>
              </td>

              <td>
                <span class="status-pill" :class="statusClass(r.status)">
                  <i class="bx" :class="statusIcon(r.status)"></i>
                  {{ r.status }}
                </span>
              </td>

              <td class="row-muted">{{ r.submitted_at }}</td>

              <td class="row-muted">
                {{ r.reviewedBy || "-" }}
              </td>

              <td style="text-align:right;">
                <div class="row-actions">
                  <button class="icon-mini" @click="openPreview(r)" title="Preview">
                    <i class="bx bx-show"></i>
                  </button>

                  <button
                    v-if="r.status === 'PENDING'"
                    class="icon-mini"
                    @click="openApprove(r)"
                    title="Approve"
                  >
                    <i class="bx bx-check-circle"></i>
                  </button>

                  <button
                    v-if="r.status === 'PENDING'"
                    class="icon-mini danger"
                    @click="openReject(r)"
                    title="Reject"
                  >
                    <i class="bx bx-x-circle"></i>
                  </button>

                  <button class="icon-mini" @click="toast.info('Open user profile screen next')" title="Open User">
                    <i class="bx bx-user"></i>
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="filtered.length === 0">
              <td colspan="6" style="padding: 18px;">
                <div class="empty">
                  <i class="bx bx-file-find"></i>
                  <div>
                    <strong>No requests found</strong>
                    <div class="row-muted">Try adjusting filters or search query.</div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Cards -->
      <div class="mobile-only list-cards">
        <div v-for="r in filtered" :key="r.user_id" class="req-card">
          <div class="top">
            <div class="usr">
              <div class="avatar" v-if="r.user_fname!=null">{{ r.user_fname.charAt(0).toUpperCase() }}</div>
              <div class="usr-meta">
                <div class="usr-name">{{ r.user_fname }} {{ r.user_lname }}</div>
                <div class="row-muted">{{ r.user_email }} • {{ r.user_mobile }}</div>
              </div>
            </div>

            <span class="status-pill" :class="statusClass(r.kycr_status)">
              <i class="bx" :class="statusIcon(r.kycr_status)"></i>
              {{ r.kycr_status }}
            </span>
          </div>

          <div class="mid">
            <span class="doc-pill">
              <i class="bx bx-id-card"></i>
              {{ r.doc_type === "AADHAR" ? "Aadhar Card" : "PAN Card" }}
            </span>

            <div class="row-muted">Doc: {{ r.doc_no }}</div>
            <div class="row-muted">Submitted: {{ r.submitted_at }}</div>
          </div>

          <div class="card-actions">
            <button class="btn ghost" @click="openPreview(r)">
              <i class="bx bx-show"></i> Preview
            </button>

            <button v-if="r.status === 'PENDING'" class="btn ghost" @click="openApprove(r)">
              <i class="bx bx-check-circle"></i> Approve
            </button>

            <button v-if="r.status === 'PENDING'" class="btn ghost" @click="openReject(r)">
              <i class="bx bx-x-circle"></i> Reject
            </button>
          </div>
        </div>

        <div v-if="filtered.length === 0" class="req-card">
          <div class="empty">
            <i class="bx bx-file-find"></i>
            <div>
              <strong>No requests found</strong>
              <div class="row-muted">Try adjusting filters or search query.</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- PREVIEW MODAL -->
   <!-- PREVIEW MODAL (MULTI DOC) -->
<div v-if="preview.open" class="modal-backdrop" @click.self="closePreview">
  <div class="modal big" v-motion-pop>
    <div class="modal-head">
      <div>
        <strong>Document Preview</strong>
        <div class="row-muted">
          {{ preview.req?.user_name }} • User #{{ preview.req?.kycr_user }}
        </div>
      </div>

      <button class="icon-mini" @click="closePreview">
        <i class="bx bx-x"></i>
      </button>
    </div>

    <div class="modal-body">
      <!-- ✅ LOADER -->
      <div v-if="preview.loading" class="empty" style="padding:20px;">
        <i class="bx bx-loader-alt bx-spin"></i>
        <div>
          <strong>Loading KYC...</strong>
          <div class="row-muted">Fetching document and user details</div>
        </div>
      </div>

      <!-- ✅ CONTENT -->
      <div v-else>
        <!-- ✅ DOC TABS -->
        <div class="doc-tabs" v-if="preview.req?.docs && preview.req?.docs.length > 0">
          <button
            v-for="d in preview.req.docs"
            :key="d.kyc_id"
            class="doc-tab"
            :class="String(d.kyc_type || '').toLowerCase() === preview.selectedType ? 'active' : ''"
            @click="preview.selectedType = String(d.kyc_type || '').toLowerCase()"
          >
            <i class="bx bx-id-card"></i>
            {{ (d.kyc_type || "").replaceAll("_", " ").toUpperCase() }}

            <span
              class="mini-status"
              :class="
                (String(d.kyc_status||'').toLowerCase()==='verified')
                  ? 'success'
                  : (String(d.kyc_status||'').toLowerCase()==='rejected')
                  ? 'danger'
                  : 'warning'
              "
            >
              {{ String(d.kyc_status || '').toUpperCase() }}
            </span>
          </button>
        </div>

        <!-- ✅ MAIN GRID -->
        <div class="preview-grid">
          <!-- LEFT: DOC IMAGE -->
          <div class="preview-card">
            <div class="pc-head">
              <strong>
                {{
                  (
                    (preview.req?.docs || []).find(
                      (x) => String(x.kyc_type || "").toLowerCase() === preview.selectedType
                    )?.kyc_type || "-"
                  )
                    .replaceAll("_", " ")
                    .toUpperCase()
                }}
              </strong>

              <span class="doc-pill">
                <i class="bx bx-image"></i> Preview
              </span>
            </div>

            <!-- ✅ SELECTED DOC -->
            <div class="img-box">
              <template
                v-if="
                  (preview.req?.docs || []).find(
                    (x) => String(x.kyc_type || '').toLowerCase() === preview.selectedType
                  )?.kyc_file
                "
              >
                <img
                  :src="base_url1+
                    (preview.req?.docs || []).find(
                      (x) => String(x.kyc_type || '').toLowerCase() === preview.selectedType
                    )?.kyc_file
                  "
                  alt="document"
                />
              </template>

              <div v-else class="empty" style="padding:18px;">
                <i class="bx bx-image-alt"></i>
                <div>
                  <strong>No file uploaded</strong>
                  <div class="row-muted">This document is missing from user submission.</div>
                </div>
              </div>
            </div>

            <!-- ✅ FILE INFO -->
            <div class="mini" style="margin-top:10px;">
              File Type:
              <b>
                {{
                  (
                    (preview.req?.docs || []).find(
                      (x) => String(x.kyc_type || '').toLowerCase() === preview.selectedType
                    )?.kyc_file_type || "-"
                  )
                }}
              </b>
            </div>
          </div>

          <!-- RIGHT: META + ACTIONS -->
          <div class="preview-side">
            <div class="hint">
              <i class="bx bx-info-circle"></i>
              <span>
                Verify document clarity, details match, and authenticity.
                Approve only if all required docs are valid.
              </span>
            </div>

            <!-- ✅ REQUEST INFO -->
            <div class="side-box">
              <div class="kv">
                <span class="k">User Name</span>
                <strong class="v">{{ preview.req?.user_name }}</strong>
              </div>

              <div class="kv">
                <span class="k">Mobile</span>
                <strong class="v">{{ preview.req?.user_mobile }}</strong>
              </div>

              <div class="kv">
                <span class="k">Submitted</span>
                <strong class="v">{{ preview.req?.kycr_submitted_at || "-" }}</strong>
              </div>

              <div class="kv">
                <span class="k">Request Status</span>
                <strong class="v">
                  <span class="status-pill" :class="statusClass(preview.req?.kycr_status)">
                    <i class="bx" :class="statusIcon(preview.req?.kycr_status)"></i>
                    {{ String(preview.req?.kycr_status || "").toUpperCase() }}
                  </span>
                </strong>
              </div>
            </div>

            <!-- ✅ SELECTED DOC DETAILS -->
            <div
              class="side-box"
              style="margin-top: 12px;"
              v-if="preview.req?.docs && preview.req?.docs.length > 0"
            >
              <div class="kv">
                <span class="k">Selected Doc Status</span>
                <strong class="v">
                  {{
                    String(
                      (preview.req?.docs || []).find(
                        (x) => String(x.kyc_type || '').toLowerCase() === preview.selectedType
                      )?.kyc_status || "-"
                    ).toUpperCase()
                  }}
                </strong>
              </div>

              <div class="kv">
                <span class="k">Remark</span>
                <strong class="v">
                  {{
                    (preview.req?.docs || []).find(
                      (x) => String(x.kyc_type || '').toLowerCase() === preview.selectedType
                    )?.kyc_remark || "-"
                  }}
                </strong>
              </div>
            </div>

            <!-- ✅ ACTIONS -->
            <div class="qa">
              <button
                class="btn ghost"
                :disabled="
                  !(preview.req?.docs || []).find(
                    (x) => String(x.kyc_type || '').toLowerCase() === preview.selectedType
                  )?.kyc_file
                "
                @click="downloadSelectedDoc"
              >
                <i class="bx bx-download"></i> Download
              </button>

              <button
                v-if="String(preview.req?.kycr_status || '').toUpperCase() === 'PENDING'"
                class="btn primary"
                @click="openApprove(preview.req)"
              >
                <i class="bx bx-check-circle"></i> Approve
              </button>

              <button
                v-if="String(preview.req?.kycr_status || '').toUpperCase() === 'PENDING'"
                class="btn ghost dangerBtn"
                @click="openReject(preview.req)"
              >
                <i class="bx bx-x-circle"></i> Reject
              </button>
            </div>
          </div>
        </div>

        <!-- ✅ NO DOCS CASE -->
        <div v-if="!preview.req?.docs || preview.req?.docs.length === 0" class="empty" style="margin-top: 12px;">
          <i class="bx bx-file-find"></i>
          <div>
            <strong>No documents found</strong>
            <div class="row-muted">This KYC request has no uploaded documents.</div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-foot">
      <button class="btn ghost" @click="closePreview">Close</button>
    </div>
  </div>
</div>


    <!-- APPROVE MODAL -->
    <div v-if="approve.open" class="modal-backdrop" @click.self="closeApprove">
      <div class="modal" v-motion-pop>
        <div class="modal-head">
          <div>
            <strong>Approve KYC</strong>
            <div class="row-muted">Confirm this user document is valid.</div>
          </div>
          <button class="icon-mini" @click="closeApprove">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="hint">
            <i class="bx bx-check-shield"></i>
            <span>
              You are approving KYC for <b>{{ approve.req?.name }}</b>.
              This action will mark user as verified.
            </span>
          </div>
        </div>

        <div class="modal-foot">
          <button class="btn ghost" @click="closeApprove">Cancel</button>
          <button class="btn primary" @click="confirmApprove">
            <i class="bx bx-check-circle"></i> Approve
          </button>
        </div>
      </div>
    </div>

    <!-- REJECT MODAL -->
    <div v-if="reject.open" class="modal-backdrop" @click.self="closeReject">
      <div class="modal" v-motion-pop>
        <div class="modal-head">
          <div>
            <strong>Reject KYC</strong>
            <div class="row-muted">Reason required for audit trail.</div>
          </div>
          <button class="icon-mini" @click="closeReject">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="field">
            <label>Rejection Reason</label>
            <textarea
              class="notes"
              v-model="reject.reason"
              placeholder="Example: Document blurry / number mismatch / invalid ID..."
            ></textarea>
          </div>
        </div>

        <div class="modal-foot">
          <button class="btn ghost" @click="closeReject">Cancel</button>
          <button class="btn primary" :disabled="!reject.reason.trim()" @click="confirmReject">
            <i class="bx bx-x-circle"></i> Reject
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, reactive, onMounted, watch } from "vue";
import { useToast } from "vue-toastification";
import { apiBaseUrl  } from "@/apis/appconst";

import {
  getKycStats,
  getKycRequests,
  getKycById,
  approveKyc,
  rejectKyc,
  exportKycRequestsXL 
} from "@/apis/kyccon";

const toast = useToast();

// ✅ Admin login data
const ldata = ref(null);
const admin_id = ref("");
const base_url1 = ref(apiBaseUrl);

// filters
const query = ref("");
const statusFilter = ref("");
const docFilter = ref("");
const sortBy = ref("NEWEST");

// data
const requests = ref([]);
const stats = reactive({
  total: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
});

// loader
const loading = reactive({
  list: false,
  stats: false,
  preview: false,
  approve: false,
  reject: false,
});

// pagination (future ready)
const page_no = ref(0);
const total_records = ref(0);

const exportExcel = async () => {
  try {
    toast.info("Preparing export...");

    const payload = {
      admin_id: JSON.parse(sessionStorage.getItem("ldata") || "{}")?.id || "1",
      keyword: query.value,
      status: statusFilter.value,
      doc_type: docFilter.value,
      sort_by: sortBy.value,
    };

    const res = await exportKycRequestsXL(payload);

    if (!res.status) {
      toast.error(res.success || "Export failed");
      return;
    }

    const blob = new Blob([res.blob], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kyc_export_${new Date().toISOString().slice(0, 19).replaceAll(":", "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    toast.success("Export downloaded ✅");
  } catch (e) {
    toast.error("Export failed");
  }
};


// ===========================
// ✅ FETCH STATS
// ===========================
const fetchStats = async () => {
  try {
    loading.stats = true;
    const res = await getKycStats(admin_id.value);

    if (res.status) {
      stats.total = parseInt(res.total || "0");
      stats.pending = parseInt(res.pending || "0");
      stats.approved = parseInt(res.approved || "0");
      stats.rejected = parseInt(res.rejected || "0");
    } else {
      toast.error(res.success || "Failed to load stats");
    }
  } catch (e) {
    toast.error("Failed to load stats");
  } finally {
    loading.stats = false;
  }
};

// ===========================
// ✅ FETCH LIST
// ===========================
const fetchRequests = async () => {
  try {
    loading.list = true;

    const res = await getKycRequests({
      admin_id: admin_id.value,
      page_no: page_no.value,
      keyword: query.value,
      status: statusFilter.value,
      doc_type: docFilter.value,
      sort_by: sortBy.value,
    });

    if (res.status) {
      requests.value = res.records || [];
      total_records.value = parseInt(res.total || "0");
    } else {
      toast.error(res.success || "Failed to load requests");
    }
  } catch (e) {
    toast.error("Failed to load requests");
  } finally {
    loading.list = false;
  }
};

const downloadSelectedDoc = () => {
  const doc = (preview.req?.docs || []).find(
    (x) => String(x.kyc_type || "").toLowerCase() === preview.selectedType
  );

  if (doc?.kyc_file) {
    const url = doc.kyc_file.startsWith("http")
      ? doc.kyc_file
      : base_url1.value + doc.kyc_file;

    window.open(url, "_blank");
  } else {
    toast.error("No file uploaded");
  }
};


// ===========================
// FILTERED (server already filters, but keep safe)
const filtered = computed(() => {
  return requests.value || [];
});

// stats from server
const pendingCount = computed(() => stats.pending);
const approvedCount = computed(() => stats.approved);
const rejectedCount = computed(() => stats.rejected);

// helpers
const statusClass = (s) => {
  if (s === "APPROVED") return "success";
  if (s === "PENDING") return "warning";
  if (s === "REJECTED") return "danger";
  return "primary";
};
const statusIcon = (s) => {
  if (s === "APPROVED") return "bx-check-circle";
  if (s === "PENDING") return "bx-time-five";
  if (s === "REJECTED") return "bx-x-circle";
  return "bx-info-circle";
};

// ===========================
// PREVIEW MODAL (fetch by id)
// ===========================
const preview = reactive({ open: false, req: null });

const openPreview = async (r) => {
  try {
    preview.open = true;
    preview.req = null;
    loading.preview = true;

    const res = await getKycById(r.kycr_id);
    if (res.status) {
      preview.req = res.kyc;
      if(res.kyc.docs.length>0){
        preview.selectedType = String(res.kyc.docs[0].kyc_type || '').toLowerCase();
      }
    } else {
      toast.error(res.success || "Failed to fetch record");
      closePreview();
    }
  } catch (e) {
    toast.error("Failed to fetch record");
    closePreview();
  } finally {
    loading.preview = false;
  }
};

const closePreview = () => {
  preview.open = false;
  preview.req = null;
};

// ===========================
// APPROVE
// ===========================
const approve = reactive({ open: false, req: null });

const openApprove = (r) => {
  approve.open = true;
  approve.req = r;
};

const closeApprove = () => {
  approve.open = false;
  approve.req = null;
};

const confirmApprove = async () => {
  try {
    if (!approve.req?.kycr_id) return;

    loading.approve = true;
    const res = await approveKyc(approve.req.kycr_id, admin_id.value);

    if (res.status) {
      toast.success(res.success || "KYC Approved ✅");
      closeApprove();
      closePreview();

      // refresh
      await fetchStats();
      await fetchRequests();
    } else {
      toast.error(res.success || "Failed to approve");
    }
  } catch (e) {
    toast.error("Failed to approve");
  } finally {
    loading.approve = false;
  }
};

// ===========================
// REJECT
// ===========================
const reject = reactive({ open: false, req: null, reason: "" });

const openReject = (r) => {
  reject.open = true;
  reject.req = r;
  reject.reason = "";
};

const closeReject = () => {
  reject.open = false;
  reject.req = null;
  reject.reason = "";
};

const confirmReject = async () => {
  try {
    if (!reject.req?.kycr_id) return;
    if (!reject.reason.trim()) return toast.error("Reason required");

    loading.reject = true;

    const res = await rejectKyc(
      reject.req.kycr_id,
      admin_id.value,
      reject.reason.trim()
    );

    if (res.status) {
      toast.success(res.success || "KYC Rejected ❌");
      closeReject();
      closePreview();

      await fetchStats();
      await fetchRequests();
    } else {
      toast.error(res.success || "Failed to reject");
    }
  } catch (e) {
    toast.error("Failed to reject");
  } finally {
    loading.reject = false;
  }
};

// ===========================
// AUTO SEARCH / FILTER refresh
// ===========================
let tmr = null;
watch([query, statusFilter, docFilter, sortBy], () => {
  clearTimeout(tmr);
  tmr = setTimeout(() => {
    page_no.value = 0;
    fetchRequests();
  }, 450);
});

// ===========================
// INIT
// ===========================
onMounted(async () => {
  try {
    const raw = sessionStorage.getItem("ldata");
    if (!raw) {
      toast.error("Session expired. Please login.");
      return;
    }

    ldata.value = JSON.parse(raw);
    admin_id.value = ldata.value?.user_id || ldata.value?.email || "";

    if (!admin_id.value) {
      toast.error("Invalid admin session");
      return;
    }

    await fetchStats();
    await fetchRequests();
  } catch (e) {
    toast.error("Failed to load page");
  }
});
</script>

<style scoped>
.actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.search-mini {
  height: 44px;
  position: relative;
  min-width: 260px;
}

.search-mini i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(148, 163, 184, 0.92);
  font-size: 18px;
}

.search-mini input {
  width: 100%;
  height: 44px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 0 12px 0 42px;
  background: rgba(255, 255, 255, 0.95);
  font-weight: var(--w-medium);
  font-size: 13px;
  outline: none;
}

.search-mini input:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

.select {
  height: 44px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.95);
  font-weight: var(--w-medium);
  font-size: 13px;
  outline: none;
}

.table-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;
}

.table-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.table-title strong {
  font-weight: var(--w-bold);
}

.muted {
  color: var(--muted);
  font-size: 12px;
  font-weight: var(--w-medium);
}

.usr {
  display: flex;
  align-items: center;
  gap: 10px;
}

.usr-meta {
  display: flex;
  flex-direction: column;
}

.usr-name {
  font-weight: var(--w-bold);
  font-size: 13px;
}

.doc-pill {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: var(--w-semibold);
  border: 1px solid var(--line);
  background: rgba(241, 245, 249, 0.65);
}

.doc-pill i {
  font-size: 16px;
  color: rgba(100, 116, 139, 0.85);
}

.doc {
  display: grid;
  gap: 6px;
}

.row-actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
}

.icon-mini {
  height: 38px;
  width: 38px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.96);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.icon-mini i {
  font-size: 18px;
  color: rgba(11, 18, 32, 0.88);
}

.icon-mini:hover {
  transform: translateY(-1px);
}

.icon-mini.danger {
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.18);
}

.icon-mini.danger i {
  color: var(--danger);
}

.empty {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 6px 2px;
}

.empty i {
  font-size: 26px;
  color: rgba(148, 163, 184, 0.9);
}

.empty strong {
  font-weight: var(--w-bold);
}

/* cards mobile */
.list-cards {
  display: none;
  margin-top: 10px;
  gap: 12px;
}

.req-card {
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  padding: 14px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: var(--shadow-sm);
}

.req-card .top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.req-card .mid {
  margin-top: 10px;
  display: grid;
  gap: 8px;
}

.card-actions {
  margin-top: 12px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* preview modal */
.modal.big {
  width: min(980px, 94vw);
}

.preview-grid {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 14px;
}

.preview-card {
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  background: rgba(255, 255, 255, 0.96);
  padding: 14px;
}

.pc-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.pc-head strong {
  font-weight: var(--w-bold);
}

.img-box {
  height: 430px;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid var(--line);
  background: rgba(246, 248, 252, 0.9);
}

.img-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-side {
  display: grid;
  gap: 12px;
}

.side-box {
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  background: rgba(255, 255, 255, 0.96);
  padding: 14px;
  display: grid;
  gap: 10px;
}

.kv .k {
  font-size: 11px;
  color: var(--muted);
  font-weight: var(--w-medium);
}

.kv .v {
  margin-top: 6px;
  display: inline-flex;
}

.qa {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.dangerBtn {
  border: 1px solid rgba(239, 68, 68, 0.18);
  background: rgba(239, 68, 68, 0.08);
  color: rgba(239, 68, 68, 0.95);
}

/* modal base */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.55);
  backdrop-filter: blur(8px);
  display: grid;
  place-items: center;
  z-index: 90;
  padding: 18px;
}

.modal {
  width: min(520px, 92vw);
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid var(--line);
  box-shadow: var(--shadow);
  padding: 16px;
}

.modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.modal-head strong {
  font-weight: var(--w-bold);
  font-size: 16px;
}

.modal-body {
  margin-top: 14px;
  display: grid;
  gap: 14px;
}

.modal-foot {
  margin-top: 14px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

/* notes textarea */
.notes {
  width: 100%;
  min-height: 110px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.96);
  padding: 12px 12px;
  outline: none;
  font-size: 13px;
  font-weight: var(--w-medium);
  resize: vertical;
}

.notes:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

.hint {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 12px;
  border-radius: 18px;
  background: rgba(46, 109, 231, 0.08);
  border: 1px solid rgba(46, 109, 231, 0.16);
  color: rgba(15, 23, 42, 0.74);
  font-size: 12px;
  font-weight: var(--w-medium);
  line-height: 1.45;
}

.hint i {
  font-size: 18px;
  color: var(--primary);
  margin-top: 1px;
}

@media (max-width: 960px) {
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
  .list-cards {
    display: grid;
  }

  .preview-grid {
    grid-template-columns: 1fr;
  }

  .img-box {
    height: 280px;
  }
}
/* ===========================================
   ✅ KYC REQUESTS MOBILE PATCH (END ONLY)
   Desktop CSS unchanged
   =========================================== */

@media (max-width: 980px) {
  /* Header stack */
  .page-head {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 12px !important;
  }

  /* Actions become vertical full width */
  .actions {
    width: 100% !important;
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px !important;
  }

  .search-mini {
    width: 100% !important;
    min-width: 0 !important;
  }

  .search-mini input {
    width: 100% !important;
  }

  .select {
    width: 100% !important;
  }

  .actions .btn.ghost {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* Stats grid */
  .grid.cols-4 {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 12px !important;
  }

  /* Table head stack */
  .table-head {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px !important;
  }

  .table-head .btn.primary {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* Mobile cards buttons */
  .card-actions .btn.ghost {
    width: 100% !important;
    justify-content: center !important;
  }

  /* ✅ Preview modal prevent horizontal overflow */
  .modal-backdrop {
    padding: 12px !important;
  }

  .modal.big {
    width: 100% !important;
    max-width: 560px !important;
    padding: 14px !important;
    border-radius: 22px !important;
  }

  .preview-grid {
    grid-template-columns: 1fr !important;
    gap: 12px !important;
  }

  .img-box {
    height: 260px !important;
  }

  /* side box + cards spacing tight */
  .preview-card,
  .side-box {
    padding: 12px !important;
    border-radius: 20px !important;
  }

  /* QA actions full width for easy tapping */
  .qa {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .qa .btn {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* approve/reject modal buttons full width */
  .modal-foot {
    flex-direction: column-reverse !important;
    align-items: stretch !important;
  }

  .modal-foot .btn {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }
}

/* ✅ extra small phones */
@media (max-width: 520px) {
  .grid.cols-4 {
    grid-template-columns: 1fr !important;
  }

  .req-card .top {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 10px !important;
  }

  .status-pill {
    width: fit-content !important;
  }

  .img-box {
    height: 220px !important;
  }
}
/* ===========================================
   ✅ PREVIEW MODAL MISSING CSS PARTS
   - Doc Tabs
   - Mini Status badge
   - Better Preview UI polish
   =========================================== */

/* ✅ DOC TABS WRAPPER */
.doc-tabs{
  display:flex;
  gap:10px;
  align-items:center;
  flex-wrap:wrap;
  margin-bottom:14px;
  padding:10px;
  border-radius:18px;
  border:1px solid var(--line);
  background: rgba(241,245,249,0.55);
}

/* ✅ SINGLE TAB BUTTON */
.doc-tab{
  display:inline-flex;
  align-items:center;
  gap:10px;
  padding:10px 14px;
  border-radius:999px;
  border:1px solid var(--line);
  background: rgba(255,255,255,0.96);
  cursor:pointer;
  font-size:12px;
  font-weight: var(--w-semibold);
  transition: all .18s ease;
  user-select:none;
  white-space:nowrap;
}

.doc-tab i{
  font-size:16px;
  color: rgba(46,109,231,0.9);
}

.doc-tab:hover{
  transform: translateY(-1px);
  box-shadow: 0 12px 30px rgba(2,6,23,0.06);
}

/* ✅ ACTIVE TAB */
.doc-tab.active{
  border-color: rgba(46,109,231,0.38);
  background: rgba(46,109,231,0.10);
  box-shadow: 0 0 0 4px rgba(46,109,231,0.10);
}

/* ✅ MINI STATUS INSIDE TAB */
.mini-status{
  margin-left:4px;
  font-size:10px;
  padding:4px 10px;
  border-radius:999px;
  border:1px solid var(--line);
  background: rgba(241,245,249,0.6);
  font-weight: var(--w-bold);
  letter-spacing: .3px;
}

/* status colors for mini-status */
.mini-status.success{
  border-color: rgba(34,197,94,0.22);
  background: rgba(34,197,94,0.12);
  color: rgba(21,128,61,0.95);
}
.mini-status.warning{
  border-color: rgba(245,158,11,0.25);
  background: rgba(245,158,11,0.14);
  color: rgba(146,64,14,0.95);
}
.mini-status.danger{
  border-color: rgba(239,68,68,0.22);
  background: rgba(239,68,68,0.12);
  color: rgba(185,28,28,0.95);
}

/* ✅ Better image container + zoom feel */
.img-box img{
  transition: transform .22s ease;
}
.img-box:hover img{
  transform: scale(1.02);
}

/* ✅ Preview modal head muted line spacing (nice alignment) */
.modal-head .row-muted{
  margin-top:4px;
  font-size:12px;
  color: rgba(100,116,139,0.9);
  font-weight: var(--w-medium);
}

/* ✅ Optional: scrollbar inside big modal if content grows */
.modal.big{
  max-height: 92vh;
  overflow: hidden;
}
.modal.big .modal-body{
  overflow: auto;
  max-height: calc(92vh - 120px);
  padding-right: 6px;
}

/* ✅ Optional: modal body scrollbar style */
.modal.big .modal-body::-webkit-scrollbar{
  width: 8px;
}
.modal.big .modal-body::-webkit-scrollbar-thumb{
  background: rgba(148,163,184,0.45);
  border-radius: 20px;
}
.modal.big .modal-body::-webkit-scrollbar-track{
  background: rgba(241,245,249,0.2);
  border-radius: 20px;
}

</style>
